// src/components/JobApplicationEnhancedPanel.tsx

import React, { useState, useEffect } from 'react';
import { useDarkMode } from '../context/DarkModeContext';
import { JobApplicationEnhanced, ApplicationStatus, Interview } from '../types/jobSearch';
import {
    IoCloseOutline,
    IoAddOutline,
    IoTrashOutline,
    IoCalendarOutline,
    IoPersonOutline,
    IoLinkOutline
} from 'react-icons/io5';

interface JobApplicationEnhancedPanelProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (application: JobApplicationEnhanced) => Promise<void>;
    application: JobApplicationEnhanced | null;
}

// Option 2: Update our status config to match database
const statusConfig = {
    saved: { label: 'Saved', color: 'bg-gray-100 text-gray-800', icon: '💾' },
    applied: { label: 'Applied', color: 'bg-blue-100 text-blue-800', icon: '📨' },
    screening: { label: 'Screening', color: 'bg-yellow-100 text-yellow-800', icon: '🔍' },
    interview_scheduled: { label: 'Interview Scheduled', color: 'bg-yellow-100 text-yellow-800', icon: '📅' },
    interview_rounds: { label: 'In Interview Rounds', color: 'bg-yellow-100 text-yellow-800', icon: '🗣️' },
    technical_assessment: { label: 'Technical Assessment', color: 'bg-purple-100 text-purple-800', icon: '💻' },
    reference_check: { label: 'Reference Check', color: 'bg-blue-100 text-blue-800', icon: '📝' },
    denied: { label: 'Denied', color: 'bg-red-100 text-red-800', icon: '❌' },
    offered: { label: 'Offered', color: 'bg-green-100 text-green-800', icon: '🎉' },
    negotiating: { label: 'Negotiating', color: 'bg-orange-100 text-orange-800', icon: '💬' },
    accepted: { label: 'Accepted', color: 'bg-green-100 text-green-800', icon: '🎯' },
    declined: { label: 'Declined', color: 'bg-pink-100 text-pink-800', icon: '👋' }
} as const;

interface StatusHistoryEntry {
    status: ApplicationStatus;
    timestamp: string;
}

interface FormDataState extends Partial<JobApplicationEnhanced> {
    is_active: boolean;  // Make this required
}

// Also add the ApplicationStatus type
type ApplicationStatus = keyof typeof statusConfig;

const JobApplicationEnhancedPanel: React.FC<JobApplicationEnhancedPanelProps> = ({
    isOpen,
    onClose,
    onSave,
    application
}) => {
    const { darkMode } = useDarkMode();
    const [newTag, setNewTag] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);
    const [formData, setFormData] = useState<FormDataState>({
        is_active: true,  // Ensure this is always defined
        company: '',
        job_title: '',
        status: 'saved' as ApplicationStatus,
        status_history: [{
            status: 'saved' as ApplicationStatus,
            timestamp: new Date().toISOString(),
        }],
        tags: [],
        interviews: [],
        application_date: new Date().toISOString().split('T')[0],
        location: '',
        salary_range: {
            min: 0,
            max: 0,
            currency: 'USD',
            period: 'yearly'
        },
        benefits: '',
        recruiter_info: {
            name: '',
            email: '',
            linkedin: ''
        },
        notes: ''
    });

    useEffect(() => {
        if (application) {
            setFormData({
                ...application,
                is_active: application.is_active ?? true  // Ensure is_active is always defined
            });
        } else {
            setFormData({
                is_active: true,
                company: '',
                job_title: '',
                status: 'saved' as ApplicationStatus,
                status_history: [{
                    status: 'saved' as ApplicationStatus,
                    timestamp: new Date().toISOString(),
                }],
                tags: [],
                interviews: [],
                application_date: new Date().toISOString().split('T')[0],
                location: '',
                salary_range: {
                    min: 0,
                    max: 0,
                    currency: 'USD',
                    period: 'yearly'
                },
                benefits: '',
                recruiter_info: {
                    name: '',
                    email: '',
                    linkedin: ''
                },
                notes: ''
            });
        }
    }, [application]);

    useEffect(() => {
        const handleEscapeKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscapeKey);

        return () => {
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isOpen, onClose]);

    const handleInputChange = (field: keyof JobApplicationEnhanced, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleAddTag = () => {
        if (newTag.trim() && !formData.tags?.includes(newTag.trim())) {
            setFormData(prev => ({
                ...prev,
                tags: [...(prev.tags || []), newTag.trim()]
            }));
            setNewTag('');
        }
    };

    const handleRemoveTag = (tagToRemove: string) => {
        setFormData(prev => ({
            ...prev,
            tags: prev.tags?.filter(tag => tag !== tagToRemove) || []
        }));
    };

    // Update the handlers
    const handleAddStatusHistory = () => {
        const newStatus = {
            status: 'applied' as ApplicationStatus,
            timestamp: new Date().toISOString()  // Already in correct format
        };
    
        const newStatusHistory = [...(formData.status_history || []), newStatus];
        newStatusHistory.sort((a, b) => 
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        );
    
        setFormData(prev => ({
            ...prev,
            status_history: newStatusHistory,
            status: newStatusHistory[newStatusHistory.length - 1].status,
            last_status_change: newStatusHistory[newStatusHistory.length - 1].timestamp
        }));
    };

    const handleStatusHistoryChange = (index: number, field: 'status' | 'timestamp', value: string) => {
        setFormData(prev => {
            const newStatusHistory = [...(prev.status_history || [])];
            newStatusHistory[index] = {
                ...newStatusHistory[index],
                [field]: field === 'timestamp' 
                    ? `${value}T00:00:00.000Z`  // Ensure timestamp is in ISO format
                    : value
            };
    
            // Sort by date after any change
            newStatusHistory.sort((a, b) => 
                new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            );
    
            return {
                ...prev,
                status_history: newStatusHistory,
                status: newStatusHistory[newStatusHistory.length - 1].status,
                last_status_change: newStatusHistory[newStatusHistory.length - 1].timestamp
            };
        });
    };

    const handleRemoveStatusHistory = (index: number) => {
        setFormData(prev => {
            const newStatusHistory = [...(prev.status_history || [])].filter((_, i) => i !== index);

            // If no status history left, set defaults
            if (newStatusHistory.length === 0) {
                return {
                    ...prev,
                    status: 'saved',
                    status_history: [],
                    last_status_change: new Date().toISOString()
                };
            }

            return {
                ...prev,
                status_history: newStatusHistory,
                status: newStatusHistory[newStatusHistory.length - 1].status,
                last_status_change: newStatusHistory[newStatusHistory.length - 1].timestamp
            };
        });
    };

    const renderStatusHistory = () => (
        <div className={`mb-6 p-4 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <h3 className="text-lg font-semibold mb-4">Status History</h3>

            <div className="grid grid-cols-2 gap-4 mb-2">
                <label className="text-sm font-medium">Status</label>
                <label className="text-sm font-medium">Date</label>
            </div>

            <div className="space-y-3">
                {formData.status_history?.map((statusEntry, index) => (
                    <div key={index} className="grid grid-cols-2 gap-4">
                        <div className="flex items-center">
                            <span className="mr-2">
                                {statusConfig[(statusEntry?.status || 'saved') as ApplicationStatus].icon}
                            </span>
                            <select
                                value={statusEntry?.status || 'saved'}
                                onChange={(e) => handleStatusHistoryChange(index, 'status', e.target.value)}
                                className={`w-full p-2 rounded ${darkMode ? 'bg-gray-600' : 'bg-white'}`}
                            >
                                {Object.entries(statusConfig).map(([status, config]) => (
                                    <option key={status} value={status}>
                                        {config.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="flex items-center">
                            <input
                                type="date"
                                value={(statusEntry?.timestamp || '').split('T')[0]}
                                onChange={(e) => handleStatusHistoryChange(index, 'timestamp', e.target.value)}
                                className={`w-full p-2 rounded ${darkMode ? 'bg-gray-600' : 'bg-white'}`}
                            />
                            <button
                                type="button"
                                onClick={() => handleRemoveStatusHistory(index)}
                                className="text-red-500 hover:text-red-600 ml-2"
                            >
                                <IoTrashOutline size={20} />
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            <button
                type="button"
                onClick={handleAddStatusHistory}
                className="text-blue-500 hover:text-blue-600 flex items-center mt-4"
            >
                <IoAddOutline size={20} className="mr-1" />
                Add Status Change
            </button>
        </div>
    );

    const handleAddInterview = () => {
        const newInterview: Interview = {
            id: crypto.randomUUID(),
            round: (formData.interviews?.length || 0) + 1,
            type: 'video',
            scheduledDate: '',
            interviewers: [],
            status: 'scheduled'
        };

        setFormData(prev => ({
            ...prev,
            interviews: [...(prev.interviews || []), newInterview]
        }));
    };

    const handleInterviewChange = (interviewId: string, field: keyof Interview, value: any) => {
        setFormData(prev => ({
            ...prev,
            interviews: prev.interviews?.map(interview =>
                interview.id === interviewId
                    ? { ...interview, [field]: value }
                    : interview
            ) || []
        }));
    };

    const handleSave = async () => {
        setIsProcessing(true);
        try {
            // Create a copy of formData and ensure status_history is properly formatted
            const formattedData = {
                ...formData,
                status_history: formData.status_history?.map(entry => ({
                    status: entry.status,
                    timestamp: entry.timestamp.includes('T')
                        ? entry.timestamp
                        : `${entry.timestamp}T00:00:00.000Z`
                }))
            };

            await onSave(formattedData as JobApplicationEnhanced);
            onClose();
        } catch (error) {
            console.error('Error saving application:', error);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className={`fixed right-0 top-0 h-full w-full md:w-2/3 lg:w-1/2 xl:w-1/3 
      ${darkMode ? 'bg-gray-800 text-white' : 'bg-white text-black'} 
      shadow-lg flex flex-col z-50 transform transition-transform duration-300
      overflow-hidden
      ${isOpen ? 'translate-x-0' : 'translate-x-full'}`}
      style={{
        height: '100%',
        minHeight: '-webkit-fill-available'
      }}>

            <div className="p-6 border-b flex justify-between items-center sticky top-0 bg-inherit z-10">
                <h2 className="text-2xl font-bold">
                    {application ? 'Edit Application' : 'New Application'}
                </h2>
                <button onClick={onClose} aria-label="Close panel">
                    <IoCloseOutline size={24} />
                </button>
            </div>

            <div className="grow overflow-y-auto p-6 pb-6 sm:pb-24">
                {/* Basic Information */}
                <section className="mb-6">
                    <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
                    <div className="space-y-4">
                        <input
                            type="text"
                            placeholder="Company"
                            value={formData.company || ''}
                            onChange={(e) => handleInputChange('company', e.target.value)}
                            className={`w-full p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                        />
                        <input
                            type="text"
                            placeholder="Job Title"
                            value={formData.job_title || ''}
                            onChange={(e) => handleInputChange('job_title', e.target.value)}
                            className={`w-full p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                        />
                        <input
                            type="text"
                            placeholder="Location"
                            value={formData.location || ''}
                            onChange={(e) => handleInputChange('location', e.target.value)}
                            className={`w-full p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                        />
                        <input
                            type="date"
                            value={formData.application_date?.split('T')[0] || ''}
                            onChange={(e) => handleInputChange('application_date', e.target.value)}
                            className={`w-full p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                        />
                    </div>
                </section>

                {/* Status */}
                {/* <section className="mb-6">
                    <h3 className="text-lg font-semibold mb-4">Status</h3>
                    <select
                        value={formData.status}
                        onChange={(e) => handleStatusChange(e.target.value as ApplicationStatus)}
                        className={`w-full p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                    >
                        {statusOptions.map(status => (
                            <option key={status} value={status}>
                                {status.charAt(0).toUpperCase() + status.slice(1).replace('_', ' ')}
                            </option>
                        ))}
                    </select>
                </section> */}
                {renderStatusHistory()}

                {/* Tags */}
                <section className="mb-6">
                    <h3 className="text-lg font-semibold mb-4">Tags</h3>
                    <div className="flex flex-wrap gap-2 mb-2">
                        {formData.tags?.map((tag) => (
                            <span
                                key={tag}
                                className={`px-2 py-1 rounded-full text-sm flex items-center
                  ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`}
                            >
                                {tag}
                                <button
                                    onClick={() => handleRemoveTag(tag)}
                                    className="ml-2 text-gray-500 hover:text-gray-700"
                                >
                                    <IoCloseOutline size={16} />
                                </button>
                            </span>
                        ))}
                    </div>
                    <div className="flex">
                        <input
                            type="text"
                            value={newTag}
                            onChange={(e) => setNewTag(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
                            placeholder="Add a tag"
                            className={`grow p-2 rounded-l ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                        />
                        <button
                            onClick={handleAddTag}
                            className="px-4 bg-blue-500 text-white rounded-r hover:bg-blue-600"
                        >
                            Add
                        </button>
                    </div>
                </section>

                {/* Salary and Benefits */}
                <section className="mb-6">
                    <h3 className="text-lg font-semibold mb-4">Compensation</h3>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <div>
                            <label className="block text-sm mb-1">Minimum Salary</label>
                            <input
                                type="number"
                                value={formData.salary_range?.min || ''}
                                onChange={(e) => handleInputChange('salary_range', {
                                    ...formData.salary_range,
                                    min: parseFloat(e.target.value)
                                })}
                                className={`w-full p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                            />
                        </div>
                        <div>
                            <label className="block text-sm mb-1">Maximum Salary</label>
                            <input
                                type="number"
                                value={formData.salary_range?.max || ''}
                                onChange={(e) => handleInputChange('salary_range', {
                                    ...formData.salary_range,
                                    max: parseFloat(e.target.value)
                                })}
                                className={`w-full p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                        <select
                            value={formData.salary_range?.currency || 'USD'}
                            onChange={(e) => handleInputChange('salary_range', {
                                ...formData.salary_range,
                                currency: e.target.value
                            })}
                            className={`w-full p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                        >
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                            <option value="GBP">GBP</option>
                            {/* Add more currencies as needed */}
                        </select>
                        <select
                            value={formData.salary_range?.period || 'yearly'}
                            onChange={(e) => handleInputChange('salary_range', {
                                ...formData.salary_range,
                                period: e.target.value as 'hourly' | 'monthly' | 'yearly'
                            })}
                            className={`w-full p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                        >
                            <option value="yearly">Yearly</option>
                            <option value="monthly">Monthly</option>
                            <option value="hourly">Hourly</option>
                        </select>
                    </div>
                    <textarea
                        placeholder="Benefits"
                        value={formData.benefits || ''}
                        onChange={(e) => handleInputChange('benefits', e.target.value)}
                        className={`w-full p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                        rows={3}
                    />
                </section>

                {/* Recruiter Information */}
                <section className="mb-6">
                    <h3 className="text-lg font-semibold mb-4">Recruiter Information</h3>
                    <div className="space-y-4">
                        <input
                            type="text"
                            placeholder="Recruiter Name"
                            value={formData.recruiter_info?.name || ''}
                            onChange={(e) => handleInputChange('recruiter_info', {
                                ...formData.recruiter_info,
                                name: e.target.value
                            })}
                            className={`w-full p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                        />
                        <input
                            type="email"
                            placeholder="Recruiter Email"
                            value={formData.recruiter_info?.email || ''}
                            onChange={(e) => handleInputChange('recruiter_info', {
                                ...formData.recruiter_info,
                                email: e.target.value
                            })}
                            className={`w-full p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                        />
                        <input
                            type="text"
                            placeholder="Recruiter LinkedIn"
                            value={formData.recruiter_info?.linkedin || ''}
                            onChange={(e) => handleInputChange('recruiter_info', {
                                ...formData.recruiter_info,
                                linkedin: e.target.value
                            })}
                            className={`w-full p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                        />
                    </div>
                </section>

                {/* Interviews */}
                <section className="mb-6">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">Interviews</h3>
                        <button
                            onClick={handleAddInterview}
                            className="flex items-center text-blue-500 hover:text-blue-600"
                        >
                            <IoAddOutline size={20} className="mr-1" />
                            Add Interview
                        </button>
                    </div>
                    <div className="space-y-4">
                        {formData.interviews?.map((interview) => (
                            <div
                                key={interview.id}
                                className={`p-4 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                            >
                                <div className="flex justify-between items-center mb-2">
                                    <h4 className="font-semibold">Round {interview.round}</h4>
                                    <button
                                        onClick={() => {
                                            setFormData(prev => ({
                                                ...prev,
                                                interviews: prev.interviews?.filter(i => i.id !== interview.id)
                                            }));
                                        }}
                                        className="text-red-500 hover:text-red-600"
                                    >
                                        <IoTrashOutline size={20} />
                                    </button>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <select
                                        value={interview.type}
                                        onChange={(e) => handleInterviewChange(interview.id, 'type', e.target.value)}
                                        className={`w-full p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                                    >
                                        <option value="phone">Phone</option>
                                        <option value="video">Video</option>
                                        <option value="onsite">On-site</option>
                                        <option value="technical">Technical</option>
                                        <option value="other">Other</option>
                                    </select>

                                    <select
                                        value={interview.status}
                                        onChange={(e) => handleInterviewChange(interview.id, 'status', e.target.value)}
                                        className={`w-full p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                                    >
                                        <option value="scheduled">Scheduled</option>
                                        <option value="completed">Completed</option>
                                        <option value="cancelled">Cancelled</option>
                                        <option value="rescheduled">Rescheduled</option>
                                    </select>

                                    <input
                                        type="datetime-local"
                                        value={interview.scheduledDate?.split('.')[0] || ''}
                                        onChange={(e) => handleInterviewChange(interview.id, 'scheduledDate', e.target.value)}
                                        className={`w-full p-2 rounded col-span-2 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                                    />

                                    {/* Interviewers */}
                                    <div className="col-span-2">
                                        <h5 className="text-sm font-semibold mb-2">Interviewers</h5>
                                        {interview.interviewers.map((interviewer, idx) => (
                                            <div key={idx} className="flex gap-2 mb-2">
                                                <input
                                                    type="text"
                                                    placeholder="Name"
                                                    value={interviewer.name}
                                                    onChange={(e) => {
                                                        const updatedInterviewers = [...interview.interviewers];
                                                        updatedInterviewers[idx] = {
                                                            ...interviewer,
                                                            name: e.target.value
                                                        };
                                                        handleInterviewChange(interview.id, 'interviewers', updatedInterviewers);
                                                    }}
                                                    className={`flex-1 p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                                                />
                                                <input
                                                    type="text"
                                                    placeholder="Role"
                                                    value={interviewer.role || ''}
                                                    onChange={(e) => {
                                                        const updatedInterviewers = [...interview.interviewers];
                                                        updatedInterviewers[idx] = {
                                                            ...interviewer,
                                                            role: e.target.value
                                                        };
                                                        handleInterviewChange(interview.id, 'interviewers', updatedInterviewers);
                                                    }}
                                                    className={`flex-1 p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                                                />
                                                <button
                                                    onClick={() => {
                                                        const updatedInterviewers = interview.interviewers.filter((_, i) => i !== idx);
                                                        handleInterviewChange(interview.id, 'interviewers', updatedInterviewers);
                                                    }}
                                                    className="text-red-500 hover:text-red-600"
                                                >
                                                    <IoTrashOutline size={20} />
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            onClick={() => {
                                                const updatedInterviewers = [
                                                    ...interview.interviewers,
                                                    { name: '', role: '' }
                                                ];
                                                handleInterviewChange(interview.id, 'interviewers', updatedInterviewers);
                                            }}
                                            className="text-blue-500 hover:text-blue-600 text-sm"
                                        >
                                            + Add Interviewer
                                        </button>
                                    </div>

                                    {/* Questions & Notes */}
                                    <div className="col-span-2 space-y-2">
                                        <textarea
                                            placeholder="Questions discussed..."
                                            value={interview.questions?.join('\n') || ''}
                                            onChange={(e) => handleInterviewChange(
                                                interview.id,
                                                'questions',
                                                e.target.value.split('\n').filter(q => q.trim())
                                            )}
                                            className={`w-full p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                                            rows={3}
                                        />
                                        <textarea
                                            placeholder="Interview notes..."
                                            value={interview.notes || ''}
                                            onChange={(e) => handleInterviewChange(interview.id, 'notes', e.target.value)}
                                            className={`w-full p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                                            rows={3}
                                        />
                                        {interview.status === 'completed' && (
                                            <textarea
                                                placeholder="Feedback..."
                                                value={interview.feedback || ''}
                                                onChange={(e) => handleInterviewChange(interview.id, 'feedback', e.target.value)}
                                                className={`w-full p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                                                rows={3}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Notes */}
                <section className="mb-6">
                    <h3 className="text-lg font-semibold mb-4">Additional Notes</h3>
                    <textarea
                        placeholder="Add any additional notes about the application..."
                        value={formData.notes || ''}
                        onChange={(e) => handleInputChange('notes', e.target.value)}
                        className={`w-full p-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}
                        rows={4}
                    />
                </section>

                {/* Mobile Footer - Part of scrollable content */}
                <div className="sm:hidden">
                    {/* Active Application Toggle */}
                    <section className="mb-6">
                        <label className="flex items-center py-2">
                            <input
                                type="checkbox"
                                checked={formData.is_active ?? true}
                                onChange={(e) => handleInputChange('is_active', e.target.checked)}
                                className="mr-2 h-5 w-5"
                            />
                            <span className="text-base">Active Application</span>
                        </label>
                    </section>

                    {/* Mobile Buttons */}
                    <div className="mt-10 mb-20 w-full grid grid-cols-2 gap-3">
                        <button
                            onClick={onClose}
                            className={`px-4 py-4 rounded-lg font-medium text-base ${darkMode ? 'bg-gray-700 text-white' : 'bg-gray-200 text-gray-800'}`}
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isProcessing}
                            className="px-4 py-4 rounded-lg font-medium text-base bg-blue-500 text-white disabled:opacity-50"
                        >
                            {isProcessing ? 'Saving...' : 'Save'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Desktop Footer Only */}
            <div className="hidden sm:flex p-6 border-t justify-between items-center">
                <label className="flex items-center">
                    <input
                        type="checkbox"
                        checked={formData.is_active ?? true}
                        onChange={(e) => handleInputChange('is_active', e.target.checked)}
                        className="mr-2"
                    />
                    Active Application
                </label>
                <div className="flex gap-2">
                    <button
                        onClick={onClose}
                        className={`px-4 py-2 rounded ${darkMode ? 'bg-gray-700' : 'bg-gray-300'}`}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isProcessing}
                        className="px-4 py-2 rounded bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
                    >
                        {isProcessing ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default JobApplicationEnhancedPanel;
